import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { get_blog, get_post_comments } from "../../../services/apis/blog";
import { useAppDispatch, useAppSelector } from "../../../store";
import Button from "../../../component/element/forms/Button";
import { FaArrowLeftLong, FaRegPenToSquare } from "react-icons/fa6";
import { LuHeart, LuMessageSquare } from "react-icons/lu";
import { real_date_time } from "../../../services/convert/date";

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const blog = useAppSelector(({ blog }) => blog.blog);
  const comments = useAppSelector(({ blog }) => blog.comments);
  const commentsTotal = useAppSelector(({ blog }) => blog.commentsTotal);
  const commentsLoading = useAppSelector(({ blog }) => blog.commentsLoading);
  const commentsError = useAppSelector(({ blog }) => blog.commentsError);

  const onLoad = React.useCallback(() => {
    if (id) {
      const blogId = Number(id);
      if (Number.isInteger(blogId) && blogId > 0) {
        get_blog(dispatch, blogId);
        get_post_comments(dispatch, blogId);
      }
    }
  }, [dispatch, id]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  return (
    <div className="w-full">
      <div className="w-full flex justify-between items-center mb-6">
        <h2 className="font-bold text-xl capitalize mb-0">Blog</h2>

        <div className="flex items-center justify-end space-x-2 ">
          {id && (
            <Button
              title="Edit"
              children={<FaRegPenToSquare className="mr-2" />}
              onClick={() => navigate(`/blog/edit/${id}`)}
              className="px-4"
            />
          )}

          <Button
            title="Back"
            children={<FaArrowLeftLong className="mr-2" />}
            onClick={() => navigate(-1)}
            className="px-4"
          />
        </div>
      </div>

      <div className="px-2 my-2 mb-4 relative grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-md overflow-hidden ">
          <div
            style={{
              backgroundImage: `url(${blog?.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            className="relative h-[400px] w-full"
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            {/* Title and Status */}
            <div className="absolute bottom-4 left-4 right-4">
              <h2 className="text-3xl font-bold text-white">{blog?.title}</h2>
            </div>
            {/* Status Badge */}
            <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs tracking-wider font-semibold bg-primary-100/70 text-primary-800 backdrop-blur-sm">
              {blog?.category?.name}
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-center justify-between font-medium mt-6">
              <div>
                <span className="mr-2">Created At:</span>
                {blog?.created_at ? real_date_time(blog.created_at) : ""}
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex items-center ">
                  <LuHeart size={20} color="red" className="mx-2" />
                  <span className="text-[1.25rem] mr-2">
                    {blog?.likes_count}
                  </span>
                </div>
                <div className="flex items-center ">
                  <LuMessageSquare size={20} color="blue" className="mx-2" />
                  <span className="text-[1.25rem] mr-2">
                    {blog?.comments_count}
                  </span>
                </div>
              </div>
            </div>

            <hr className="text-gray-200 mt-6" />

            <div
              dangerouslySetInnerHTML={{ __html: blog?.article || "" }}
              className="mt-6 text-base"
            />
          </div>
        </div>

        {/* Comments Section */}
        <div className="overflow-hidden rounded-xl bg-white shadow-sm">
          <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
            <h2 className="font-semibold text-gray-800">
              Comments ({blog?.comments_count ?? commentsTotal})
            </h2>
          </div>

          {commentsLoading ? (
            <div className="p-4">
              <p className="text-sm text-gray-500">Loading comments...</p>
            </div>
          ) : commentsError ? (
            <div className="p-4">
              <p className="text-sm text-red-600">{commentsError}</p>
            </div>
          ) : comments.length === 0 ? (
            <div className="p-4">
              <p className="text-sm text-gray-500">No comments yet.</p>
            </div>
          ) : (
            <div>
              <div className="h-[400px] overflow-y-auto p-4">
                {comments.map((comment) => (
                  <div
                    key={comment.id}
                    className="mb-4 rounded-lg border border-gray-100 bg-white p-3 last:mb-0 hover:bg-gray-50"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        aria-hidden="true"
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 font-medium text-gray-600 ring-2 ring-gray-100"
                      >
                        {comment.author_name?.trim().charAt(0).toUpperCase() ||
                          "?"}
                      </div>
                      <div className="flex-1 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">
                            {comment.author_name || "Anonymous"}
                          </span>
                          <span className="text-xs text-gray-500">
                            {comment.created_at
                              ? real_date_time(comment.created_at)
                              : ""}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{comment.body}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogDetails;
